import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import React, { memo, useMemo } from 'react'

import styles from './TicketCard.module.sass'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import FuseHighlight from 'components/FuseHighlight/FuseHighlight'
import Tooltip from '@jimmyboys/liita-ui/dist/components/Tooltip'
import { useReload } from 'store'

export const getTitleByTicketType = (ticketType) => {
	const drawerMap = new Map([
		[1, 'Ambulatory Quote'],
		[2, 'Surgical Quote'],
		[3, 'Transports'],
		[4, 'Contact Claimant'],
		[5, 'Contact Provider'],
		[6, 'Clarification'],
		[7, 'Clinical Clarification'],
		[8, 'Technical Assistance'],
		[9, 'Medical Acts Valuation'],
	])

	return drawerMap.get(ticketType)
}

export const getStateValueByTicketState = (statusId) => {
	const drawerMap = new Map([
		[1, 'opened'],
		[2, 'awaiting-validation'],
		[3, 'under-review'],
		[4, 'approved'],
		[5, 'concluded'],
		[6, 'voided'],
	])

	return drawerMap.get(statusId)
}

const getOptions = (i18n, data, showDialog, reload) => {
	const simpleOptions = [
		[
			{
				value: 'cancel',
				text: i18n`Nullify ticket`,
				action: () => {
					showDialog(
						'TicketNullifyRequestDialog',
						i18n`Nullify ticket`,
						{
							processId: data.processId,
							ticketId: data.id,
							childs: data.childs,
						},
						false,
						undefined,
						'center',
						'nullify-ticket-dialog'
					)
						.then(() => {
							reload([
								`/clinical_processes/${data.processId}/tickets`,
								`/clinical_processes/${data.processId}/tasks`,
								`/metadata/tickets/${data.id}`,
							])
						})
						.catch(() => null)
				},
			},
		],
	]

	if (data.ticketType > 2) {
		// Orçamentos são o 1 e 2
		simpleOptions[0].unshift({
			value: 'ticket',
			text: i18n`Open associated ticket`,
			action: () => {
				showDialog(
					'TicketRequestDialog',
					i18n`Ticket Request Dialog ${data.id}`,
					{
						processId: data.processId,
						ticketId: data.id,
					},
					false,
					undefined,
					'center',
					'request-ticket-dialog'
				)
					.then(() => {
						reload([
							`/clinical_processes/${data.processId}/tickets`,
							`/clinical_processes/${data.processId}/tasks`,
							`/metadata/tickets/${data.id}`,
						])
					})
					.catch(() => null)
			},
		})
	}

	return simpleOptions
}

const renderTooltipTicketsContent = (
	appointment,
	requisition,
	parentTicket,
	i18n
) => {
	if (appointment) {
		return (
			<VStack>
				<small>
					{i18n`Appointment`} #{appointment.id}
				</small>
				<small>{appointment.medicalSpecialty}</small>
				<small>{appointment.date.dateTimeFormat}</small>
			</VStack>
		)
	}

	if (parentTicket) {
		return (
			<VStack>
				<small>
					{i18n`Ticket`} #{parentTicket.id} (
					{i18n.translate(
						getTitleByTicketType(parentTicket.ticketType)
					)}
					)
				</small>
				<small>{parentTicket.date.dateTimeFormat}</small>
			</VStack>
		)
	}

	return (
		<VStack>
			<small>
				{i18n`Requisition`} #{requisition.id}
			</small>
			<small>{requisition.requisitionType}</small>
			<small>{requisition.date.shortDateFormat}</small>
		</VStack>
	)
}

const TicketCard = memo(
	(props) => {
		const { className, data, ...attributes } = props
		const { showDialog, i18n } = useGlobal()
		const reload = useReload()
		const titles = [i18n`Actions`]

		const labels = {
			title: i18n.translate(getTitleByTicketType(data?.ticketType)),
			status: i18n.translate(
				getStateValueByTicketState(data?.ticketStatus?.id)
			),
		}

		const actionOptions = useMemo(() => {
			return getOptions(i18n, data, showDialog, reload)
		}, [i18n, data, showDialog, reload])

		const showOptions = data?.ticketStatus?.id < 5

		return (
			<>
				<HStack
					{...attributes}
					className={`card ${styles['root'] || ''} ${
						className || ''
					}`}
					justify='space-between'
					gap='small'
					columns={[1, 2, 1]}
					onClick={() => {
						data.$action && data.$action()
					}}
				>
					<VStack gap='small'>
						<HStack gap='xsmall'>
							<h5>{labels.title}</h5>
							{data?.hasParent && (
								<Tooltip
									content={renderTooltipTicketsContent(
										data.appointment,
										data.requisition,
										data.parentTicket,
										i18n
									)}
								>
									<div>
										<Icon
											width='20px'
											height='20px'
											name='chain'
										/>
									</div>
								</Tooltip>
							)}
							{data?.childs?.length && (
								<Icon
									className={styles['horizontal-icon']}
									width='20px'
									height='20px'
									name='ticket'
								/>
							)}
						</HStack>

						<VStack>
							<span>
								<small>
									{i18n`Asked at ${data?.creationDate?.shortDateFormat}`}
								</small>
							</span>
							<span>
								<small>{i18n`Asked by ${data?.author}`}</small>
							</span>
						</VStack>
					</VStack>

					<div dynamic='true' className={styles['comment-body']}>
						{data.description && (
							<FuseHighlight
								matchedWords={
									data.$textHighlight?.matchedIndexes?.[
										'description'
									]
								}
								text={data?.description}
							/>
						)}
					</div>

					<VStack justify={showOptions ? 'space-around' : 'flex-end'}>
						{showOptions && (
							<ModalOptions
								className={styles['actions-modal']}
								titles={titles}
								corner='straight'
								options={actionOptions}
								groups={actionOptions}
								onSelect={(item) => {
									item && item.action && item.action()
								}}
							>
								<button className={styles['ticket-options']}>
									<Icon
										className={styles['more-icon']}
										width='32px'
										height='32px'
										name={'more'}
									/>
								</button>
							</ModalOptions>
						)}

						<VStack>
							<h5
								className={`${styles['ticket-status']} ${
									styles[`status-${labels.status}`]
								}`}
							>
								{data?.ticketStatus?.description}
							</h5>
							<small className={styles['ticket-id']}>
								#{data?.id}
							</small>
						</VStack>
					</VStack>
				</HStack>
			</>
		)
	},
	(prevProps, nextProps) => prevProps.data === nextProps.data
)

export default TicketCard
